import ComponentMixin from '../smart/component.m.js';
const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;

class Base extends HTMLElement {
	constructor(...args) {
		super(...args);
	}
}

/**
 * Dialog web component implements a standard <dialog> element if not supported natively
 * @extends HTMLElement
 * @example <zs-dialog open>Hi!</zs-dialog>
 * @example let el = document.createElement('zs-dialog').init(); el.show();
 * @copyright: Copyright (c) 2019 ZS Associates; Copyright (c) 2013 The Chromium Authors. All rights reserved.
 */
export default class Dialog extends ComponentMixin(Base) {

	/**
	 * Create an instance. Custom Elements API requirements.
	 * @param  {...any} args 
	 */
	constructor(...args) {
		const _ = super(...args);

		return _;
	}

	/**
	 * Use it with or instead of a constructor to avoid [constructor caveat](https://github.com/ungap/custom-elements-builtin)
	 * @returns {Dialog} - Itself to enable chaining
	 * @example let el = document.createElement('zs-dialog').init();
	 */
	setup() {
		super.setup();

		// We use click and keydown events internally but expose custom events `close`, `cancel` and `open`
		this.on(['click', 'keydown', 'close', 'cancel', 'open'], this);

		/**
		 * @type {boolean} open - Display the dialog. Reflected in attributes
		 */
		this.syncProp('open');

		/**
		 * @type {boolean} modal - Indicates if the dialog should be shown in the modal mode. Reflected in attributes
		 */
		this.syncProp('modal');

		/**
		 * @type {string} returnValue - Return value of a dialog.
		 */
		this.returnValue = '';

		this._zIndexMin = 100000;
		this._zIndexStep = 100;
		return this;
	}

	/**
	 * Register a custom element and add necessary styles.
	 * @param {?string} is Name of the new custom element `<my-element>` or `<div is="my-element">`
	 * @param {?string} tagName Name of the tag use only when customizing built-ins e.g. `<div is="my-element">`
	 */
	static register(is, tagName) {
		let Cls = this;
		is = is || 'zs-dialog';
		let style = document.createElement('style');
		style.innerHTML = `${is} {${Cls.styles}}
		.backdrop[for="${is}"]  {${Cls.backdropStyles}}
		${is}.modal {
			left:50%;
			top:50%;
			margin:0!important;
			transform: translate(-50%, -50%);
			position:fixed;	
		}
		${is}:not([open]) {
			display:none!important;
		}
		`;
		document.head.appendChild(style);
		if (window.HTMLDialogElement && is == 'dialog') { throw 'Native <dialog> element is available.' }
		Cls.prototype._dialogTag = is;

		return super.register(is, tagName);
	}

	/**
	 * Callback triggered when this element is attached to the DOM. Can be called multiple times. Custom Elements API.
	 */
	connectedCallback() {
		super.connectedCallback();
		let self = this;
		// Set a11y role. Browsers that support dialog implicitly know this already.
		if (!this.hasAttribute('role')) {
			this.setAttribute('role', 'dialog');
		}

		if (self.open) {
			// Fix: Need to wait till children are added to the DOM
			window.requestAnimationFrame(() => {
				self._show(self.modal);
			});
		}
	}

	/**
	 * Callback triggered when this element is detached from the DOM. Can be called multiple times. Custom Elements API.
	 */
	disconnectedCallback() {
		if (this._backdrop) {
			this._backdrop.parentElement.removeChild(this._backdrop);
			this._backdrop = null;
		}
	}

	/**
	 * @type {array} observedAttributes - Observed attributes. Custom Elements API.
	 */
	static get observedAttributes() {
		return super.observedAttributes.concat(['open', 'onclose', 'oncancel', 'onopen', 'modal']);
	}

	/**
	 * Return a property type by name. Used in the sync attributes and properties flow.
	 * @param {*} name - Property name in a lower camel case.
	 * @returns {string} - Property type. Supports `boolean`, `string`, `number`.
	 */
	getPropertyType(name) {
		switch (name) {
			case 'open':
			case 'modal':
				return 'boolean';
			default:
				return 'string';
		}
	}

	/**
	 * Callback triggered when an observed attributed was changed. Custom Elements API.	 
	 * @param {string} name Attribute name in dash lower case e.g. `my-attribute`
	 * @param {string} oldValue Old value of the attribute
	 * @param {string} newValue New value of the attribute
	 */
	attributeChangedCallback(name, oldValue, newValue) {
		super.attributeChangedCallback(name, oldValue, newValue);
		if (name == 'onclose' || name == 'oncancel' || name == 'onopen') {
			let _onabort = this.onabort;
			let _onabortAttr = this.getAttribute('onabort');
			this.setAttribute('onabort', newValue);
			this[name] = this.onabort;
			this.onabort = _onabort;

			if (_onabortAttr == null) {
				this.removeAttribute('onabort');
			} else {
				this.setAttribute('onabort', _onabortAttr);
			}
			return;
		}
		this.syncAttr(name, newValue, this.getPropertyType(name));
	}

	/**
	 * Callback triggered when synchronized property or attribute was changed.
	 * @param {string} name Property name in lower camel case e.g. `myProperty`
	 * @param {number|boolean|string} oldValue Old value of the property. 
	 * @param {number|boolean|string} newValue New value of the property.
	 */
	propertyChangedCallback(name, oldValue, newValue) {
		super.propertyChangedCallback(name, oldValue, newValue);
		switch (name) {
			case 'modal':
				if (this.open && newValue != oldValue) {
					// Re-open with or without a backdrop
					this._close();
					if (this.parentElement) { this._show(newValue); }
				}
				break;
			case 'open':
				if (newValue) {
					if (!(this.modal && !this.parentElement)) {
						this._show(this.modal);
					}
				} else if (oldValue != null) {
					this._close();
				}

		}
	}

	/**
	 * @type {number} zIndex - Used to update the stacking order or a modal dialog to bring it on top.
	 * @private
	 */
	get zIndex() {
		if (this._zIndex == null) {
			// Detect the highest zIndex of all dialogs
			let dialogs = document.querySelectorAll(`dialog, ${this._dialogTag}`);
			let newValue = this._zIndexMin;
			for (let i = 0; i < dialogs.length; i++) {
				let zIndex = ~~window.getComputedStyle(dialogs[i]).zIndex;
				if (newValue <= zIndex) {
					newValue = zIndex + this._zIndexStep;
				}
			}
			this._zIndex = newValue;
		}
		return this._zIndex;
	}
	set zIndex(newValue) {
		if (newValue == null) {
			this.style.zIndex = '';
			if (this._backdrop) { this._backdrop.style.zIndex = ''; }
		} else {
			this.style.zIndex = newValue;
			if (this._backdrop) { this._backdrop.style.zIndex = newValue; }
		}
		this._zIndex = newValue;
	}

	/**
	 * Show a modal dialog and make other necessary actions like showing a backdrop, auto focusing, setting up form.
	 * @private
	 * @param {boolean} modal - Flag indicating a modal mode
	 * @fires open
	 */
	_show(modal) {
		var self = this;
		if (modal) {
			// Show backdrop only when attached to the dom

			//to avoid coverity issues we need below statement, since we need to execute the getter backdrop
			this.backdrop = this.backdrop;
			this.root.classList.add('modal');

		} else {
			this.root.classList.remove('modal');
		}
		this.root.style.display = isIE11 ? 'inline-block' : 'block'; // IE11 doesn't support fit-content

		self.autofocus();

		// Close on submit
		let form = this.root.querySelector('form[method="dialog"]');
		if (form) {
			this.on('submit', this, form);
		}


		if (modal) {
			// Reset stacking order
			this.zIndex = this.zIndex;

			// Adjust position if too tall and top corner goes beyond the screen
			let rects = this.getClientRects();
			if (rects[0] && rects[0].top < 0 && !this._topMargin) {
				this._topMargin = -1 * rects[0].top;
				// Need to change `style` attribute to have `!important` working
				this.setAttribute('style', this.getAttribute('style') + ';margin-top:' + this._topMargin + 'px !important');
			}

		}


		/**
		 * 
		 * Open event. Supports `onopen` event handlers and attributes.
		 * 
		 * @event open
		 * @type {Event}
		 */
		this.fire('open', {
			bubbles: false,
			cancelable: false
		});

	}

	/**
	 * Show a dialog in a regular mode.
	 * @fires open
	 */
	show() {
		if (this.open) { return; }
		if (!this.parentElement && this.modal) { throw 'Failed to execute \'show\' in modal mode'; }
		this.modal = false;
		this.open = true;
	}

	/**
	 * Show a dialog in a modal mode
	 * @fires open
	 */
	showModal() {
		if (!this.parentElement || this.open) { throw 'Failed to execute \'showModal\''; }
		this.modal = true;
		this.open = true;
	}

	/**
	 * Close the modal dialog and remove the backdrop
	 * @private
	 * @fires close
	 */
	_close() {
		this.root.style.display = 'none';


		// Triggering "close" event for any attached listeners on the <dialog>.
		this.fire('close', {
			bubbles: false,
			cancelable: false
		});

		// Hide backdrop
		if (this._backdrop) {
			this.zIndex = null; // reset z-index only for modal
			this._backdrop.parentElement.removeChild(this._backdrop);
			this._backdrop = null;
		}

		this._submitterValue = undefined;

		let form = this.root.querySelector('form[method="dialog"]');
		if (form) {
			this.off('submit', this, form);
		}
	}

	/**
	 * Close the dialog and optionally return value
	 * @param {?any} result - Value for dialog to return. Typically, a submit button value would be used.
	 */
	close(result) {
		if (result !== undefined) {
			this.returnValue = result;
		}
		this.open = false;
	}

	/**
	 * Get a backdrop element and create it if needed
	 * @type {HTMLDivElement}
	 */
	get backdrop() {
		// Insert backdrop.
		if (!this._backdrop) {
			this._backdrop = document.createElement('div');
			this._backdrop.className = 'backdrop';
			this._backdrop.setAttribute('for', this.tagName.toLowerCase());
			this.on('click', this, this._backdrop);
			this.root.parentNode.insertBefore(this._backdrop, this.root);
		}

		return this._backdrop;
	}
	set backdrop(ele) {
		this._backdrop = ele;
	}
	/**
	 * Root element. Normally this
	 * @type {HTMLTableElement}
	 */
	get root() {
		return this;
	}


	/**
	 * Focuses on the first focusable element within the dialog. This will always blur the current
	 * focus, even if nothing within the dialog is found.
	 */
	autofocus() {
		this.removeAttribute('tabindex');
		this.tabIndex = 0;
		if (!this.parentElement) { return; }

		// Find element with `autofocus` attribute, or fall back to the first form/tabindex control.
		var target = this.root.querySelector('[autofocus]:not([disabled])');
		if (!target) {
			// Note that this is 'any focusable area'. This list is probably not exhaustive, but the
			// alternative involves stepping through and trying to focus everything.
			var opts = ['button', 'input', 'keygen', 'select', 'textarea'];
			var query = opts.map(function (el) {
				return el + ':not([disabled])';
			});
			// TODO(samthor): tabindex values that are not numeric are not focusable.
			query.push('[tabindex]:not([disabled]):not([tabindex=""])');  // tabindex != "", not disabled
			target = this.root.querySelector(query.join(', '));
		}
		if (!target) { // Focus on itself to enable keydown events
			target = this;
		}
		if (document.activeElement != target) {
			if (document.activeElement) { document.activeElement.blur(); }
			target && target.focus();
		}
	}

	/**
	 * Click event handler. Used for backdrop focus and return value logic.
	 * @param {Event} event 
	 */
	onclick(event) {
		// Backdrop click
		if (this.modal && event.target == this._backdrop) {
			if (this.hasAttribute('tabindex')) {
				this.focus();
			}

			var redirectedEvent = document.createEvent('MouseEvents');
			redirectedEvent.initMouseEvent(event.type, false, event.cancelable, window,
				event.detail, event.screenX, event.screenY, event.clientX, event.clientY, event.ctrlKey,
				event.altKey, event.shiftKey, event.metaKey, event.button, event.relatedTarget);
			this.dispatchEvent(redirectedEvent);
			event.stopPropagation();
		}

		// Remember value of the submitter
		if (event.target.type === 'submit' && ['button', 'input'].indexOf(event.target.localName) > -1) {
			this._submitterValue = event.target.value;
		} else if (event.target.type === 'image' && event.target.localName === 'input') {
			this._submitterValue = event.offsetX + ',' + event.offsetY;
		}
	}

	/**
	 * Keydown event handler. Used to handle close on ESC button.
	 * @param {Event} event 
	 */
	onkeydown(event) {
		this.forwardTab_ = undefined;
		if (this.modal && (event.key === 'Escape' || event.keyCode === 27)) {
			event.preventDefault();
			event.stopPropagation();
			this.fire('cancel', {
				bubbles: false,
				cancelable: true
			});
			this.close();
		} else if (event.key === 'Tab' || event.keyCode === 9) {
			this.forwardTab_ = !event.shiftKey;
		}
	}

	/**
	 * @type {string} styles - Minimum required styles for dialogs to work.
	 */
	static get styles() {
		// Note: `fit-content` is not supported by IE11 so display `inline-block` with no margins and adjust positioning could be used as a workaround
		return `
			position: absolute;
			left: 0; 
			right: 0;
			width: fit-content; 
			height: fit-content;
			width: -moz-fit-content;
			width: -webkit-fit-content;			
			height: -moz-fit-content;
			height: -webkit-fit-content;
			margin: auto;
			border: solid;
			padding: 1em;
			background: white;
			color: black;
			${isIE11 ? `
				left:50%;
				margin:0;
				transform: translateX(-50%); 
			` : ``}	
		`;
	}

	/**
	 * @type {string} backdropStyles - Minimum required styles for backdrop to work.
	 */
	static get backdropStyles() {
		return `
			position: fixed;
			top: 0; right: 0; bottom: 0; left: 0;
			background: rgba(0,0,0,0.1);
		`
	}

	/**
	 * Sets the zIndex for the backdrop and dialog.
	 * @param {number} dialogZ
	 * @param {number} backdropZ
	 */
	updateZIndex(dialogZ, backdropZ) {
		if (dialogZ < backdropZ) {
			throw new Error('dialogZ should never be < backdropZ');
		}
		this.root.style.zIndex = dialogZ;
		this.backdrop.style.zIndex = backdropZ;
	}

	/**
	 * Submit event handler. Helps with returning a value from the dialog.
	 * @param {SubmitEvent} event 
	 */
	onsubmit(event) {
		this.close(this._submitterValue);
		if (event) { event.preventDefault(); } // can be called manually when polyfilling native form submit
	}

	/**
	 * Open event handler. 
	 * @override
	 * @param {CustomEvent} event
	 */
	onopen(event) { }

	/**
	 * Cancel event handler
	 * @override
	 * @param {CustomEvent} event
	 */
	oncancel(event) { }

	/**
	 * Close event handler
	 * @override
	 * @param {CustomEvent} event
	 */
	onclose(event) { }
}


/**
  * Replace the native HTMLFormElement.submit() method, as it won't fire the
  * submit event and give us a chance to respond.
  */
var nativeFormSubmit = HTMLFormElement.prototype.submit;
var replacementFormSubmit = function () {
	if (!isFormMethodDialog(this)) {
		return nativeFormSubmit.call(this);
	}
	var dialog = findNearestDialog(this);
	dialog && (dialog.onsubmit ? dialog.onsubmit() : dialog.close());
};
HTMLFormElement.prototype.submit = replacementFormSubmit;

/**
 * @param {HTMLFormElement} el to check
 * @return {boolean} whether this form has method="dialog"
 */
function isFormMethodDialog(el) {
	if (!el || !el.hasAttribute('method')) {
		return false;
	}
	return el.getAttribute('method').toLowerCase() === 'dialog';
}


/**
* Finds the nearest <dialog> from the passed element.
*
* @param {Element} el to search from
* @return {HTMLDialogElement} dialog found
*/
function findNearestDialog(el) {
	while (el) {
		if (el.localName === Dialog.prototype._dialogTag) {
			return /** @type {HTMLDialogElement} */ (el);
		}
		el = el.parentElement;
	}
	return null;
}

/**
 * If HTMLFormElement translates method="DIALOG" into 'get', then replace the descriptor with
 * one that returns the correct value.
 */
var testForm = document.createElement('form');
testForm.setAttribute('method', 'dialog');
if (testForm.method !== 'dialog') {
	var methodDescriptor = Object.getOwnPropertyDescriptor(HTMLFormElement.prototype, 'method');
	if (methodDescriptor) {
		// nb. Some older iOS and older PhantomJS fail to return the descriptor. Don't do anything
		// and don't bother to update the element.
		var realGet = methodDescriptor.get;
		methodDescriptor.get = function () {
			if (isFormMethodDialog(this)) {
				return 'dialog';
			}
			return realGet.call(this);
		};
		var realSet = methodDescriptor.set;
		methodDescriptor.set = function (v) {
			if (typeof v === 'string' && v.toLowerCase() === 'dialog') {
				return this.setAttribute('method', v);
			}
			return realSet.call(this, v);
		};
		Object.defineProperty(HTMLFormElement.prototype, 'method', methodDescriptor);
	}
}
testForm = null;
Dialog.is = 'zs-dialog';
